import {Suspense, lazy, useEffect, useState} from 'react'
import {Spinner} from '@primer/react'
import {useParams} from '@github-ui/react-router'
import {fetchPoll} from '@github-ui/fetch-utils'
import type {CommitActivityDataPoint, CommitMetricDataPoints} from '../types'
import {Blankslate} from '@primer/react/experimental'
import {GraphIcon} from '@primer/octicons-react'

const CommitActivityChart = lazy(async () => {
  const module = await import('../components/CommitActivity')
  return {default: module.default}
})

const ErrorMessage = ({errorMessage}: {errorMessage: string}) => (
  <div className="text-center pr-p-3" data-hpc>
    <div className="msg">
      <p>{errorMessage}</p>
    </div>
  </div>
)

const Loading = () => (
  <div className="text-center pr-p-3" data-hpc>
    <Spinner />
    <div className="graph-loading msg">
      <p>Crunching the latest data, just for you. Hang tight…</p>
    </div>
  </div>
)

export function Index() {
  const {owner, repo} = useParams()
  const [isLoaded, setIsLoaded] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [commits, setCommits] = useState<CommitMetricDataPoints[]>([])

  const graphDataPath = `/${owner}/${repo}/graphs/commit-activity-data`

  useEffect(() => {
    const fetcher = async () => {
      if (!graphDataPath) {
        setErrorMessage('Graph data path is not available')
        setIsLoaded(true)
        return
      }
      try {
        const response = await fetchPoll(graphDataPath)
        if (response.ok) {
          const data: CommitActivityDataPoint[] = await response.json()
          setCommits(data.map(item => [item.week * 1000, item.total]))
        } else {
          const {unusable} = await response.json()
          if (unusable) {
            setErrorMessage('Data is unusable')
          } else {
            setErrorMessage('There was an error fetching the data')
          }
        }
      } catch {
        setErrorMessage('An unexpected error occurred while fetching the data')
      } finally {
        setIsLoaded(true)
      }
    }
    fetcher()
  }, [graphDataPath])

  if (errorMessage) {
    return <ErrorMessage errorMessage={errorMessage} />
  }

  if (isLoaded && commits.length === 0) {
    return (
      <div data-hpc>
        <Blankslate>
          <Blankslate.Visual>
            <GraphIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading>Sorry, No Data Found</Blankslate.Heading>
          <Blankslate.Description>This repository has no recorded commit activity.</Blankslate.Description>
        </Blankslate>
      </div>
    )
  }

  if (!isLoaded) {
    return <Loading />
  }

  return (
    <>
      <h2 className="text-normal d-flex flex-row flex-items-center gap-2 flex-wrap pr-mb-4" data-hpc>
        <div>
          <span>Commits over the last year of </span>
          <strong>
            {owner}/{repo}
          </strong>
        </div>
      </h2>
      <Suspense>
        <CommitActivityChart commits={commits} />
      </Suspense>
    </>
  )
}
